import React, { useState, useEffect, useRef } from 'react';

// Main App Component for Course Instructional Design Assistance
const App = () => {
  // State to manage the current view: 'outline' (chat), 'lessons' (table), 'validation', 'suggestions', 'quizGenerator', 'scriptAssistant', 'report'
  const [currentView, setCurrentView] = useState('outline');

  // State for the Course Outline (from chat)
  const [courseOutlineData, setCourseOutlineData] = useState({
    title: '',
    targetAudience: '',
    needsAnalysis: '',
    learningObjectives: [], // Stored as an array of strings
    contentOutline: '',
    assessmentPlan: '',
    iacetConsiderations: "myPLCtraining training include in-video prompts, and there is a community that students can post question and discussion forum, the courses of myplctraining.com hosted in Kajabi.",
  });

  // State for the chat interface (CourseCraft AI part)
  const [userInput, setUserInput] = useState('');
  const [chatHistory, setChatHistory] = useState([
    {
      role: 'model',
      parts: [
        {
          text: "Welcome to **Course Instructional Design Assistance**! Let's start by outlining your online, asynchronous course. What is the **title** of your course?",
        },
      ],
    },
  ]);
  const [currentOutlineStep, setCurrentOutlineStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const chatEndRef = useRef(null);

  // Steps for the Course Outline (similar to CourseCraft AI)
  const outlineSteps = [
    {
      id: 'title',
      prompt: 'What is the official **title** of your course?',
      label: 'Course Title',
      placeholder: 'e.g., Mastering Public Speaking for Professionals',
      dataKey: 'title',
    },
    {
      id: 'targetAudience',
      prompt: 'Please define your **target audience**. Include their professional background, existing knowledge base, and specific learning needs or skill gaps this course aims to address.',
      label: 'Target Audience',
      placeholder: 'e.g., Mid-career professionals who struggle with confidence in presentations.',
      dataKey: 'targetAudience',
    },
    {
      id: 'needsAnalysis',
      prompt: 'Based on the identified audience, what specific **problem or deficiency** does this course aim to resolve? What is the core need that drives the development of this learning intervention?',
      label: 'Needs Analysis',
      placeholder: 'e.g., Lack of engaging presentation skills leading to missed opportunities.',
      dataKey: 'needsAnalysis',
    },
    {
      id: 'learningObjectives',
      prompt: 'Now, articulate the **key learning objectives** for this course. These should be measurable, action-oriented statements describing what learners will *be able to perform* upon successful completion. Please provide them as a concise list.',
      label: 'Learning Objectives',
      placeholder: 'e.g., Deliver compelling presentations, Manage nerves effectively, Adapt message to different audiences.',
      dataKey: 'learningObjectives',
    },
    {
      id: 'contentOutline',
      prompt: 'Next, provide a **high-level overview of the course content**. Outline the main modules or thematic units that will comprise this asynchronous, video-recorded course. Consider the logical flow and progression of topics.',
      label: 'Overall Content Outline',
      placeholder: 'e.g., Module 1: Foundations; Module 2: Delivery; Module 3: Audience Adaptation.',
      dataKey: 'contentOutline',
    },
    {
      id: 'assessmentPlan',
      prompt: 'Regarding **assessment**, how will learning for the *entire course* be evaluated? Describe the format of the final examination (e.g., multiple-choice, scenario-based) and its alignment with the overall learning objectives. How will this assessment be administered and graded within an **LMS** environment?',
      label: 'Overall Assessment Plan',
      placeholder: 'e.g., 20 multiple-choice questions covering key concepts, 70% passing score. Includes a scenario-based question, graded automatically by LMS.',
      dataKey: 'assessmentPlan',
    },
    {
      id: 'iacetConsiderations',
      prompt: 'For **IACET standards and best practices** related to this online, asynchronous, video-recorded course hosted on Kajabi, myPLCtraining.com **already includes in-video prompts, and there is a community where students can post questions and use a discussion forum**. Please confirm if this information is accurate, or provide any additional details for IACET alignment or continuous improvement feedback mechanisms.',
      label: 'IACET & Best Practices',
      placeholder: 'e.g., Confirmed. We also track video watch time and quiz attempts.',
      dataKey: 'iacetConsiderations',
    },
  ];

  // State for the Lesson Details (from CourseTable Designer part)
  const [lessonsData, setLessonsData] = useState([
    {
      lesson: '',
      topic: '',
      timeEnvironment: '',
      contentDescription: '',
      learningOutcomes: '',
      instructionalMaterials: '',
      methodLearningStyles: '',
      assessmentCriteria: '',
    },
  ]);

  // State for the final combined report (will contain Markdown + HTML Table string)
  const [combinedReport, setCombinedReport] = useState('');
  // State for the lessons table CSV
  const [lessonsTableCsv, setLessonsTableCsv] = useState('');
  const lessonsTableCsvRef = useRef(null);
  const [csvActionMessage, setCsvActionMessage] = useState('Copy Lessons as CSV'); // State for CSV copy message

  // New states for validation and suggestions reports
  const [validationReport, setValidationReport] = useState('');
  const [validationReportDownloadMessage, setValidationReportDownloadMessage] = useState('Download Report File'); // New state for validation report download button
  const [aiSuggestionsReport, setAiSuggestionsReport] = useState('');
  const [aiSuggestionsCopyMessage, setAiSuggestionsCopyMessage] = useState('Copy to Clipboard'); // State for AI Suggestions copy message
  const [applyRecsMessage, setApplyRecsMessage] = useState('Apply Recommendations to Lesson Plan'); // New state for apply recommendations button message

  // New states for Quiz Question Generator
  const [quizLessonIndex, setQuizLessonIndex] = useState(0); // Index of lesson to generate quiz for
  const [generatedQuizQuestions, setGeneratedQuizQuestions] = useState('');

  // New states for Video Script Assistant
  const [scriptLessonIndex, setScriptLessonIndex] = useState(0); // Index of lesson to generate script for
  const [generatedVideoScript, setGeneratedVideoScript] = useState('');
  const [scriptCopyMessage, setScriptCopyMessage] = useState('Copy to Clipboard'); // State for Script copy message

  // State for Full Report Copy Message
  const [reportCopyMessage, setReportCopyMessage] = useState('Copy to Clipboard');

  // Email action messages
  const [emailLessonPlanMessage, setEmailLessonPlanMessage] = useState('Email Lesson Plan');

  // Effect to scroll chat to the bottom when new messages are added
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatHistory]);

  // --- Functions for Course Outline (Chat) ---
  const sendOutlineMessage = async () => {
    if (!userInput.trim()) return;

    setIsLoading(true);

    const userMessage = { role: 'user', parts: [{ text: userInput }] };
    const newChatHistory = [...chatHistory, userMessage];
    setChatHistory(newChatHistory);
    setUserInput(''); // Clear input field

    // Update courseOutlineData based on the current step's dataKey
    const currentStepConfig = outlineSteps[currentOutlineStep];
    if (currentStepConfig && currentStepConfig.dataKey) {
      if (currentStepConfig.dataKey === 'learningObjectives') {
        // Special handling for learning objectives, split by common delimiters
        const objectives = userInput.split(/, |; |\n(?=\*?-? ?|\d+\.? ?)/).map(obj => obj.trim()).filter(obj => obj !== '');
        setCourseOutlineData(prev => ({ ...prev, [currentStepConfig.dataKey]: objectives }));
      } else {
        setCourseOutlineData(prev => ({ ...prev, [currentStepConfig.dataKey]: userInput }));
      }
    }

    try {
      // Prepare the payload for the Gemini API call
      const prompt = `You are an expert and professional Instructional Designer. Your role is to guide the user through a structured instructional design process for an online, video-recorded, asynchronous course hosted on an LMS. Maintain a formal, informative, and precise tone, focusing on key instructional design principles and IACET standards.

      Review the current instructional design outline progress and the user's latest input. Provide a professional response to their input for "${currentStepConfig.label}". After your response, if applicable, present the next question/prompt clearly and directly, using professional language.

      If all outline steps are complete, provide a conclusive statement indicating completion of the high-level outline and suggest proceeding to the next phases of course development (detailing lessons, validation, or full report generation).

      ---
      Current instructional design outline progress:
      ${Object.entries(courseOutlineData).map(([key, value]) => {
        if (Array.isArray(value)) {
          return `${key}: ${value.join('; ')}`;
        }
        return `${key}: ${value}`;
      }).join('\n')}

      User input for "${currentStepConfig.label}": "${userInput}"

      The next prompt to ask (if not the last step) is: "${outlineSteps[currentOutlineStep + 1]?.prompt || 'The high-level course outline is now complete. You may proceed to detailing individual lessons, validating your design, or generating the full instructional design report for comprehensive review.'}"`;

      const payload = {
        contents: [
          ...chatHistory.slice(0, chatHistory.length),
          { role: 'user', parts: [{ text: prompt }] },
        ],
      };

      // Changed apiKey to an empty string to allow Canvas to inject it
      const apiKey = "AIzaSyBjZEUkQMyjxXfjJL6GDEIB7B9LKP-b3fA";
      const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (result.candidates && result.candidates.length > 0 &&
          result.candidates[0].content && result.candidates[0].content.parts &&
          result.candidates[0].content.parts.length > 0) {
        const modelResponseText = result.candidates[0].content.parts[0].text;
        setChatHistory(prev => [...prev, { role: 'model', parts: [{ text: modelResponseText }] }]);
        setCurrentOutlineStep(prev => prev + 1); // Move to the next step
      } else {
        console.error('Unexpected API response structure:', result);
        setChatHistory(prev => [...prev, { role: 'model', parts: [{ text: 'An error occurred while processing your request. Please try again.' }] }]);
      }
    } catch (error) {
      console.error('Error fetching from Gemini API:', error);
      setChatHistory(prev => [...prev, { role: 'model', parts: [{ text: 'Failed to establish connection with the AI. Please verify your network and try again.' }] }]);
    } finally {
      setIsLoading(false);
    }
  };

  // --- Functions for Lesson Details (Table) ---
  const autoGenerateLessons = async () => {
    setIsLoading(true);

    const prompt = `Based on the following high-level course outline details for an online, asynchronous, video-recorded course hosted on an LMS, generate a detailed lesson plan in a JSON array format. Each object in the array should represent one lesson and have the following keys: "lesson", "topic", "timeEnvironment", "contentDescription", "learningOutcomes", "instructionalMaterials", "methodLearningStyles", "assessmentCriteria".

    The "lesson" field should be something like "Module 1, Lesson 1".
    The "timeEnvironment" should suggest time for an asynchronous video module.
    The "contentDescription" should outline the video content.
    The "learningOutcomes" should be a multi-line string for the lesson, using action verbs appropriate for specific lesson outcomes.
    The "instructionalMaterials" should suggest video content, downloadable readings, or interactive activities manageable within an LMS.
    The "methodLearningStyles" should suggest teaching methods suitable for asynchronous learning (e.g., self-paced video, guided practice, reflection) and the learning styles they accommodate.
    The "assessmentCriteria" should describe how the lesson's learning outcomes will be assessed, considering the LMS environment (e.g., short quiz, graded activity, discussion post).

    Course Outline Title: ${courseOutlineData.title}
    Overall Learning Objectives: ${courseOutlineData.learningObjectives.join('; ')}
    Overall Content Outline: ${courseOutlineData.contentOutline}
    Overall Assessment Plan: ${courseOutlineData.assessmentPlan}

    Please generate at least 3-5 initial lessons that break down the overall content into manageable topics, keeping the asynchronous, LMS-based nature in mind. Ensure the suggested lessons align with the overall course objectives and content.
    `;

    try {
        const payload = {
            contents: [{ role: 'user', parts: [{ text: prompt }] }],
            generationConfig: {
                responseMimeType: "application/json",
                responseSchema: {
                    type: "ARRAY",
                    items: {
                        type: "OBJECT",
                        properties: {
                            "lesson": { "type": "STRING" },
                            "topic": { "type": "STRING" },
                            "timeEnvironment": { "type": "STRING" },
                            "contentDescription": { "type": "STRING" },
                            "learningOutcomes": { "type": "STRING" },
                            "instructionalMaterials": { "type": "STRING" },
                            "methodLearningStyles": { "type": "STRING" },
                            "assessmentCriteria": { "type": "STRING" }
                        },
                        "propertyOrdering": [
                            "lesson", "topic", "timeEnvironment", "contentDescription",
                            "learningOutcomes", "instructionalMaterials", "methodLearningStyles",
                            "assessmentCriteria"
                        ]
                    }
                }
            }
        };

        // Changed apiKey to an empty string to allow Canvas to inject it
        const apiKey = "AIzaSyBjZEUkQMyjxXfjJL6GDEIB7B9LKP-b3fA";
        const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`;
        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });
        const result = await response.json();

        if (result.candidates && result.candidates.length > 0 &&
            result.candidates[0].content && result.candidates[0].content.parts &&
            result.candidates[0].content.parts.length > 0) {
            const jsonString = result.candidates[0].content.parts[0].text;
            try {
                const parsedLessons = JSON.parse(jsonString);
                setLessonsData(parsedLessons);
            } catch (jsonError) {
                console.error('Error parsing JSON from LLM:', jsonError);
                setLessonsData([{
                  lesson: '', topic: '', timeEnvironment: '', contentDescription: '',
                  learningOutcomes: '', instructionalMaterials: '', methodLearningStyles: '',
                  assessmentCriteria: ''
                }]);
            }
        } else {
            console.error('Unexpected API response structure for lesson generation:', result);
            setLessonsData([{
              lesson: '', topic: '', timeEnvironment: '', contentDescription: '',
              learningOutcomes: '', instructionalMaterials: '', methodLearningStyles: '',
              assessmentCriteria: ''
            }]);
        }
    } catch (error) {
        console.error('Error auto-generating lessons:', error);
        setLessonsData([{
          lesson: '', topic: '', timeEnvironment: '', contentDescription: '',
          learningOutcomes: '', instructionalMaterials: '', methodLearningStyles: '',
          assessmentCriteria: ''
        }]);
    } finally {
        setIsLoading(false);
    }
  };

  const addLesson = () => {
    setLessonsData([
      ...lessonsData,
      {
        lesson: '',
        topic: '',
        timeEnvironment: '',
        contentDescription: '',
        learningOutcomes: '',
        instructionalMaterials: '',
        methodLearningStyles: '',
        assessmentCriteria: '',
      },
    ]);
  };

  const handleLessonChange = (index, field, value) => {
    const newLessons = [...lessonsData];
    newLessons[index][field] = value;
    setLessonsData(newLessons);
  };

  const handleProceedToLessons = () => {
    setCurrentView('lessons');
    if (lessonsData.length === 1 && lessonsData[0].lesson === '' && lessonsData[0].topic === '') {
      autoGenerateLessons();
    }
  };

  // Function to generate *only* the lessons data in CSV format
  const generateLessonsCsv = () => {
    if (lessonsData.length === 0) {
      setLessonsTableCsv('No lessons to generate CSV for.');
      return ''; // Return empty string if no data
    }

    const headers = [
      'Lesson',
      'Topic',
      'Time Allotted/Learning Environment',
      'Need Content Description and/or Purpose',
      'List Learning Outcomes: Upon Successful completion of this course, you will be able to:',
      'Instructional Materials Used',
      'Method used along with the learning styles that are accommodated with this method',
      'Assessment method and performance criteria',
    ];

    const escapeCsv = (value) => {
      if (value === null || value === undefined) return '';
      const stringValue = String(value);
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    };

    const headerRow = headers.map(escapeCsv).join(',');
    const dataRows = lessonsData.map(lesson => {
      return [
        escapeCsv(lesson.lesson),
        escapeCsv(lesson.topic),
        escapeCsv(lesson.timeEnvironment),
        escapeCsv(lesson.contentDescription),
        escapeCsv(lesson.learningOutcomes),
        escapeCsv(lesson.instructionalMaterials),
        escapeCsv(lesson.methodLearningStyles),
        escapeCsv(lesson.assessmentCriteria),
      ].join(',');
    }).join('\n');

    const fullCsv = `${headerRow}\n${dataRows}`;
    setLessonsTableCsv(fullCsv);
    return fullCsv; // Return the generated CSV string
  };

  const copyLessonsCsvToClipboard = () => {
    const csvContent = generateLessonsCsv(); // Ensure CSV is generated right before copying
    if (csvContent) {
      const textarea = document.createElement('textarea');
      textarea.value = csvContent;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      setCsvActionMessage('Copied! Paste into Excel/Google Sheets'); // Update button text
      setTimeout(() => setCsvActionMessage('Copy Lessons as CSV'), 3000); // Revert after 3 seconds
      document.body.removeChild(textarea);
    }
  };

  // Reusable function to download text as a file
  const downloadTextAsFile = (text, filename, mimeType, setMessage) => {
    if (!text) {
      if (setMessage) {
        setMessage('Nothing to download!');
        setTimeout(() => setMessage('Download File'), 2000);
      }
      return;
    }
    const blob = new Blob([text], { type: mimeType });
    const link = document.createElement('a');
    if (link.download !== undefined) { // feature detection
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      if (setMessage) {
        setMessage('Downloaded!');
        setTimeout(() => setMessage('Download File'), 2000);
      }
    } else {
      // Fallback for browsers that do not support download attribute
      // Using a custom modal/message box instead of alert()
      const message = `Your browser does not support direct file downloads. Please use the 'Copy to Clipboard' option and paste the content into a file, or try a different browser.`;
      // In a real app, you'd show a custom modal here
      console.warn(message);
      if (setMessage) {
        setMessage('Download failed (browser support)!');
        setTimeout(() => setMessage('Download File'), 3000);
      }
    }
  };

  const downloadLessonsCsv = () => {
    const csvContent = generateLessonsCsv(); // Ensure CSV is generated right before downloading
    downloadTextAsFile(csvContent, 'detailed_lesson_plan.csv', 'text/csv;charset=utf-8;', setCsvActionMessage);
  };


  const copyToClipboard = (text, setMessage) => {
    if (!text) {
      if (setMessage) {
        setMessage('Nothing to copy!');
        setTimeout(() => setMessage('Copy to Clipboard'), 2000);
      }
      return;
    }
    const textarea = document.createElement('textarea');
    textarea.value = text;
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    if (setMessage) {
      setMessage('Copied!');
      setTimeout(() => setMessage('Copy to Clipboard'), 2000);
    }
    document.body.removeChild(textarea);
  };

  // Reusable function to email content
  const emailContent = (recipient, subject, body, setMessage) => {
    if (!body) {
      if (setMessage) {
        setMessage('Nothing to email!');
        setTimeout(() => setMessage('Email Content'), 2000);
      }
      return;
    }
    const mailtoLink = `mailto:${recipient}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(mailtoLink, '_blank');
    if (setMessage) {
      setMessage('Email client opened!');
      setTimeout(() => setMessage('Email Content'), 3000);
    }
  };

  // --- Function to generate the validation report ---
  const generateValidationReport = async () => {
    setIsLoading(true);
    setCurrentView('validation'); // Switch to validation view
    setValidationReportDownloadMessage('Download Report File'); // Reset message
    setApplyRecsMessage('Apply Recommendations to Lesson Plan'); // Reset apply recommendations message

    const outlineString = Object.entries(courseOutlineData).map(([key, value]) => {
      if (Array.isArray(value)) {
        return `**${key.replace(/([A-Z])/g, ' $1').trim()}**: ${value.join('; ')}`;
      }
      return `**${key.replace(/([A-Z])/g, ' $1').trim()}**: ${value}`;
    }).join('\n\n');

    let lessonTableString = '';
    if (lessonsData.length > 0 && lessonsData[0].lesson !== '') { // Only include if lessons are actually populated
      lessonTableString = '## Detailed Lesson Plan:\n\n';
      lessonsData.forEach((lesson, index) => {
        lessonTableString += `### Lesson ${index + 1}: ${lesson.lesson || 'Untitled'}\n`;
        lessonTableString += `- **Topic**: ${lesson.topic || 'N/A'}\n`;
        lessonTableString += `- **Time/Environment**: ${lesson.timeEnvironment || 'N/A'}\n`;
        lessonTableString += `- **Content Purpose**: ${lesson.contentDescription || 'N/A'}\n`;
        lessonTableString += `- **Learning Outcomes**: ${lesson.learningOutcomes || 'N/A'}\n`;
        lessonTableString += `- **Materials**: ${lesson.instructionalMaterials || 'N/A'}\n`;
        lessonTableString += `- **Method/Styles**: ${lesson.methodLearningStyles || 'N/A'}\n`;
        lessonTableString += `- **Assessment**: ${lesson.assessmentCriteria || 'N/A'}\n\n`;
      });
    }

    const prompt = `As an expert instructional designer and quality assurance specialist knowledgeable in IACET standards, review the following course instructional design details for an **online, asynchronous, video-recorded course hosted on an LMS**.

    Provide a concise validation report, covering:
    1.  **Strengths**: What aspects of the design are well-aligned, clear, and follow best practices?
    2.  **Areas for Improvement**: Identify any potential gaps, inconsistencies, ambiguities, or areas where the design could be strengthened. Focus on:
        * Alignment between stated needs, learning objectives, content, and assessments, especially for self-paced learning.
        * Clarity and measurability of learning objectives suitable for asynchronous evaluation.
        * Feasibility and appropriateness of instructional methods for an **asynchronous, video-recorded course on an LMS**. Consider how engagement is maintained without live interaction.
        * Adequacy of assessment methods to truly measure stated outcomes, given LMS capabilities for monitoring progress.
        * Consideration of learner engagement and feedback mechanisms (relevant to IACET), emphasizing asynchronous methods.
    3.  **Actionable Recommendations**: For each area for improvement, provide specific, actionable suggestions.

    Format your response with clear headings for Strengths, Areas for Improvement, and Actionable Recommendations.

    ---
    **Course Outline Details:**
    ${outlineString}

    **Detailed Lesson Plan:**
    ${lessonTableString || 'No detailed lesson plan provided yet.'}
    ---
    `;

    try {
      const payload = {
        contents: [{ role: 'user', parts: [{ text: prompt }] }],
      };

      // Changed apiKey to an empty string to allow Canvas to inject it
      const apiKey = "AIzaSyBjZEUkQMyjxXfjJL6GDEIB7B9LKP-b3fA";
      const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (result.candidates && result.candidates.length > 0 &&
          result.candidates[0].content && result.candidates[0].content.parts &&
          result.candidates[0].content.parts.length > 0) {
        setValidationReport(result.candidates[0].content.parts[0].text);
      } else {
        console.error('Unexpected API response structure for validation report:', result);
        setValidationReport('Error: Could not get a validation report. Please try again.');
      }
    } catch (error) {
      console.error('Error generating validation report:', error);
      setValidationReport('Error: Failed to generate the validation report. Please check your network and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Function to download the validation report
  const downloadValidationReport = () => {
    // Generate HTML content from the validationReport Markdown
    const processedHtmlContent = validationReport.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\n/g, '<br/>');

    const htmlContent = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Validation Report</title>
    <script src="https://cdn.tailwindcss.com"></script>
    <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap" rel="stylesheet">
    <style>
        body {
            font-family: 'Inter', sans-serif;
            background-color: #f3f4f6;
            padding: 2rem;
            line-height: 1.6;
            color: #374151;
        }
        .container {
            max-width: 800px;
            margin: 0 auto;
            background-color: #ffffff;
            padding: 2rem;
            border-radius: 0.5rem;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
        }
        .prose h1, .prose h2, .prose h3, .prose h4, .prose h5, .prose h6 {
          color: #1f2937;
          margin-top: 1.5em;
          margin-bottom: 0.5em;
        }
        .prose h1 { font-size: 2.25rem; }
        .prose h2 { font-size: 1.875rem; }
        .prose h3 { font-size: 1.5rem; }
        .prose strong {
          color: #1f2937;
        }
        .prose ul, .prose ol {
          padding-left: 1.5em;
          margin-top: 0.5em;
          margin-bottom: 1em;
        }
        .prose li {
          margin-bottom: 0.0em;
        }
        .prose p {
          margin-bottom: 1em;
        }
    </style>
</head>
<body>
    <div class="container prose">
        ${processedHtmlContent}
    </div>
</body>
</html>`;

    downloadTextAsFile(htmlContent, 'validation_report.doc', 'application/msword', setValidationReportDownloadMessage);
  };

  // --- Function to apply validation recommendations to lessonsData ---
  const applyValidationRecommendations = async () => {
    setIsLoading(true);
    setApplyRecsMessage('Applying...');

    const outlineString = Object.entries(courseOutlineData).map(([key, value]) => {
      if (Array.isArray(value)) {
        return `${key}: ${value.join('; ')}`;
      }
      return `${key}: ${value}`;
    }).join('\n');

    const lessonsDataJson = JSON.stringify(lessonsData, null, 2); // Pretty print JSON

    const prompt = `Based on the following existing detailed lesson plan (JSON) and the validation report's "Actionable Recommendations," generate an UPDATED detailed lesson plan in a JSON array format. Each object in the array should represent one lesson and have the following keys: "lesson", "topic", "timeEnvironment", "contentDescription", "learningOutcomes", "instructionalMaterials", "methodLearningStyles", "assessmentCriteria".

    Crucially, incorporate the "Actionable Recommendations" from the validation report into the new lesson plan. If a recommendation is about the overall course outline and doesn't directly map to a lesson, you can note it or adapt it into relevant lesson fields. Focus on making the lessons more robust, aligned, and engaging as per the recommendations.

    Do not include any conversational text, only the JSON array.

    ---
    Existing Course Outline Data:
    ${outlineString}

    Existing Detailed Lesson Plan (JSON):
    ${lessonsDataJson}

    Validation Report Recommendations (from "Actionable Recommendations" section):
    ${validationReport}
    ---
    `;

    try {
      const payload = {
          contents: [{ role: 'user', parts: [{ text: prompt }] }],
          generationConfig: {
              responseMimeType: "application/json",
              responseSchema: {
                  type: "ARRAY",
                  items: {
                      type: "OBJECT",
                      properties: {
                          "lesson": { "type": "STRING" },
                          "topic": { "type": "STRING" },
                          "timeEnvironment": { "type": "STRING" },
                          "contentDescription": { "type": "STRING" },
                          "learningOutcomes": { "type": "STRING" },
                          "instructionalMaterials": { "type": "STRING" },
                          "methodLearningStyles": { "type": "STRING" },
                          "assessmentCriteria": { "type": "STRING" }
                      },
                      "propertyOrdering": [
                          "lesson", "topic", "timeEnvironment", "contentDescription",
                          "learningOutcomes", "instructionalMaterials", "methodLearningStyles",
                          "assessmentCriteria"
                      ]
                  }
              }
          }
      };

      // Changed apiKey to an empty string to allow Canvas to inject it
      const apiKey = "AIzaSyBjZEUkQMyjxXfjJL6GDEIB7B9LKP-b3fA";
      const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`;
      const response = await fetch(apiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
      });
      const result = await response.json();

      if (result.candidates && result.candidates.length > 0 &&
          result.candidates[0].content && result.candidates[0].content.parts &&
          result.candidates[0].content.parts.length > 0) {
          const jsonString = result.candidates[0].content.parts[0].text;
          try {
              const parsedLessons = JSON.parse(jsonString);
              setLessonsData(parsedLessons);
              // Set success message and prompt next step
              setApplyRecsMessage('Recommendations applied! Proceed to Generate Full Report.');
              setTimeout(() => setApplyRecsMessage('Apply Recommendations to Lesson Plan'), 5000); // Revert after 5 seconds
              setCurrentView('lessons'); // Optionally switch to lessons view to show updates
          } catch (jsonError) {
              console.error('Error parsing JSON from LLM:', jsonError);
              setApplyRecsMessage('Error: Invalid AI JSON. Try again.');
              setTimeout(() => setApplyRecsMessage('Apply Recommendations to Lesson Plan'), 3000);
          }
      } else {
          console.error('Unexpected API response structure for applying recommendations:', result);
          setApplyRecsMessage('Error applying recs. Try again.');
          setTimeout(() => setApplyRecsMessage('Apply Recommendations to Lesson Plan'), 3000);
      }
    } catch (error) {
        console.error('Error applying recommendations:', error);
        setApplyRecsMessage('Network error. Try again.');
        setTimeout(() => setApplyRecsMessage('Apply Recommendations to Lesson Plan'), 3000);
    } finally {
        setIsLoading(false);
    }
  };


  // --- New function to generate AI suggestions for lessons/storyline ---
  const getAiSuggestions = async () => {
    setIsLoading(true);
    setCurrentView('suggestions'); // Switch to suggestions view
    setAiSuggestionsCopyMessage('Copy to Clipboard'); // Reset message

    const outlineString = Object.entries(courseOutlineData).map(([key, value]) => {
      if (Array.isArray(value)) {
        return `**${key.replace(/([A-Z])/g, ' $1').trim()}**: ${value.join('; ')}`;
      }
      return `**${key.replace(/([A-Z])/g, ' $1').trim()}**: ${value}`;
    }).join('\n\n');

    let lessonTableString = '';
    if (lessonsData.length > 0 && lessonsData[0].lesson !== '') { // Only include if lessons are actually populated
      lessonTableString = '## Detailed Lesson Plan:\n\n';
      lessonsData.forEach((lesson, index) => {
        lessonTableString += `### Lesson ${index + 1}: ${lesson.lesson || 'Untitled'}\n`;
        lessonTableString += `- **Topic**: ${lesson.topic || 'N/A'}\n`;
        lessonTableString += `- **Time/Environment**: ${lesson.timeEnvironment || 'N/A'}\n`;
        lessonTableString += `- **Content Purpose**: ${lesson.contentDescription || 'N/A'}\n`;
        lessonTableString += `- **Learning Outcomes**: ${lesson.learningOutcomes || 'N/A'}\n`;
        lessonTableString += `- **Materials**: ${lesson.instructionalMaterials || 'N/A'}\n`;
        lessonTableString += `- **Method/Styles**: ${lesson.methodLearningStyles || 'N/A'}\n`;
        lessonTableString += `- **Assessment**: ${lesson.assessmentCriteria || 'N/A'}\n\n`;
      });
    }

    const prompt = `As an expert instructional designer and creative consultant for **online, asynchronous, video-recorded courses hosted on an LMS**, analyze the following course instructional design outline and detailed lesson plan. Your task is to provide concrete, actionable suggestions on how to **improve the lessons and the overall storyline/flow**.

    Focus on:
    * **Enhancing Lesson Content**: What specific additions, refinements, or interactive elements could make each lesson more impactful, engaging, or clear, specifically for self-paced video modules?
    * **Improving Storyline and Flow**: How can the progression between lessons be made more seamless, logical, or narrative-driven for an asynchronous experience? Are there opportunities for a stronger course 'arc' or connecting themes across modules?
    * **Creative Instructional Methods**: Suggest innovative ways to deliver content or activities that leverage video format and LMS capabilities (e.g., branching scenarios, simulated exercises, peer reviews via discussion forums). Accommodate diverse learning styles and boost engagement in a non-live setting.
    * **Assessment Integration**: Ideas for how assessments could be more creatively woven into the learning experience or how feedback mechanisms could be enhanced within the LMS to support continuous learning.

    Provide your suggestions with clear headings for each area, making them highly practical and easy to implement.

    ---
    **Course Outline Details:**
    ${outlineString}

    **Detailed Lesson Plan:**
    ${lessonTableString || 'No detailed lesson plan provided yet.'}
    ---
    `;

    try {
      const payload = {
        contents: [{ role: 'user', parts: [{ text: prompt }] }],
      };

      // Changed apiKey to an empty string to allow Canvas to inject it
      const apiKey = "AIzaSyBjZEUkQMyjxXfjJL6GDEIB7B9LKP-b3fA";
      const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (result.candidates && result.candidates.length > 0 &&
          result.candidates[0].content && result.candidates[0].content.parts &&
          result.candidates[0].content.parts.length > 0) {
        setAiSuggestionsReport(result.candidates[0].content.parts[0].text);
      } else {
        console.error('Unexpected API response structure for AI suggestions:', result);
        setAiSuggestionsReport('Error: Could not get AI suggestions. Please try again.');
      }
    } catch (error) {
      console.error('Error generating AI suggestions:', error);
      setAiSuggestionsReport('Error: Failed to generate AI suggestions. Please check your network and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // --- Function to generate Quiz Questions ---
  const generateQuizQuestions = async () => {
    setIsLoading(true);
    setCurrentView('quizGenerator'); // Switch to quiz generator view

    if (lessonsData.length === 0 || quizLessonIndex === -1 || !lessonsData[quizLessonIndex]) {
      setGeneratedQuizQuestions('Please ensure you have detailed lessons and select a valid lesson to generate questions for.');
      setIsLoading(false);
      return;
    }

    const selectedLesson = lessonsData[quizLessonIndex];
    const prompt = `As an expert in assessment design for **online, asynchronous, LMS-hosted courses**, generate 3-5 multiple-choice questions for the following lesson. Ensure questions directly assess the stated learning outcomes and are suitable for an automated LMS quiz. Provide a correct answer for each question.

    Course Title: ${courseOutlineData.title}
    Lesson Title: ${selectedLesson.lesson}
    Lesson Topic: ${selectedLesson.topic}
    Lesson Learning Outcomes: ${selectedLesson.learningOutcomes}
    Lesson Content Description: ${selectedLesson.contentDescription}

    Format your response clearly, with each question followed by options (A, B, C, D) and then the correct answer identified.`;

    try {
      const payload = {
        contents: [{ role: 'user', parts: [{ text: prompt }] }],
      };

      // Changed apiKey to an empty string to allow Canvas to inject it
      const apiKey = "AIzaSyBjZEUkQMyjxXfjJL6GDEIB7B9LKP-b3fA";
      const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const result = await response.json();

      if (result.candidates && result.candidates.length > 0 && result.candidates[0].content && result.candidates[0].content.parts && result.candidates[0].content.parts.length > 0) {
        setGeneratedQuizQuestions(result.candidates[0].content.parts[0].text);
      } else {
        console.error('Unexpected API response structure for quiz questions:', result);
        setGeneratedQuizQuestions('Error: Could not generate quiz questions. Please try again.');
      }
    } catch (error) {
      console.error('Error generating quiz questions:', error);
      setGeneratedQuizQuestions('Error: Failed to generate quiz questions. Please check your network and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // --- Function to generate Video Script ---
  const generateVideoScript = async () => {
    setIsLoading(true);
    setCurrentView('scriptAssistant'); // Switch to script assistant view
    setScriptCopyMessage('Copy to Clipboard'); // Reset message

    if (lessonsData.length === 0 || scriptLessonIndex === -1 || !lessonsData[scriptLessonIndex]) {
      setGeneratedVideoScript('Please ensure you have detailed lessons and select a valid lesson to draft a script for.');
      setIsLoading(false);
      return;
    }

    const selectedLesson = lessonsData[scriptLessonIndex];
    const prompt = `As an expert in creating engaging video content for **online, asynchronous courses (like those on myPLCtraining.com)**, draft a concise video script for the following lesson. The script should focus on clarity, learner engagement, and convey key information for self-paced consumption. Include suggestions for visual aids or on-screen text. Aim for a script that would translate to a 5-7 minute video.

    Course Title: ${courseOutlineData.title}
    Lesson Title: ${selectedLesson.lesson}
    Lesson Topic: ${selectedLesson.topic}
    Lesson Learning Outcomes: ${selectedLesson.learningOutcomes}
    Lesson Content Description: ${selectedLesson.contentDescription}
    Suggested Instructional Materials: ${selectedLesson.instructionalMaterials}

    Format the script with clear sections (e.g., Introduction, Key Point 1, Conclusion), including cues for visuals/on-screen text.`;

    try {
      const payload = {
        contents: [{ role: 'user', parts: [{ text: prompt }] }],
      };

      // Changed apiKey to an empty string to allow Canvas to inject it
      const apiKey = "AIzaSyBjZEUkQMyjxXfjJL6GDEIB7B9LKP-b3fA";
      const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const result = await response.json();

      if (result.candidates && result.candidates.length > 0 && result.candidates[0].content && result.candidates[0].content.parts && result.candidates[0].content.parts.length > 0) {
        setGeneratedVideoScript(result.candidates[0].content.parts[0].text);
      } else {
        console.error('Unexpected API response structure for video script:', result);
        setGeneratedVideoScript('Error: Could not draft video script. Please try again.');
      }
    } catch (error) {
      console.error('Error generating video script:', error);
      setGeneratedVideoScript('Error: Failed to draft video script. Please check your network and try again.');
    } finally {
      setIsLoading(false);
    }
  };


  // --- Function to generate the final combined report ---
  const generateCombinedReport = async () => {
    setIsLoading(true);
    setCurrentView('report'); // Switch to report view
    setReportCopyMessage('Copy to Clipboard'); // Reset message

    // Prepare the structured data for the LLM
    const outlineString = Object.entries(courseOutlineData).map(([key, value]) => {
      if (Array.isArray(value)) {
        return `- **${key.replace(/([A-Z])/g, ' $1').trim()}**: ${value.join('; ')}`;
      }
      return `- **${key.replace(/([A-Z])/g, ' $1').trim()}**: ${value}`;
    }).join('\n');

    // --- Generate HTML table for lessons (explicit HTML for html-docx-js) ---
    let combinedReportLessonTableHtml = '';
    if (lessonsData.length > 0 && lessonsData[0].lesson !== '') { // Only include if lessons are actually populated
      combinedReportLessonTableHtml = `
        <h2 style="font-size: 1.875rem; color: #1f2937; margin-top: 1.5em; margin-bottom: 0.5em;">Detailed Lesson Plan</h2>
        <table style="width:100%; border-collapse: collapse; margin-top: 1em; margin-bottom: 1em; table-layout: auto;">
            <thead>
                <tr style="background-color: #f9fafb; color: #4b5563; font-weight: 600; text-align: left;">
                    <th style="border: 1px solid #e5e7eb; padding: 8px; vertical-align: top;">Lesson</th>
                    <th style="border: 1px solid #e5e7eb; padding: 8px; vertical-align: top;">Topic</th>
                    <th style="border: 1px solid #e5e7eb; padding: 8px; vertical-align: top;">Time/Environment</th>
                    <th style="border: 1px solid #e5e7eb; padding: 8px; vertical-align: top;">Content Description</th>
                    <th style="border: 1px solid #e5e7eb; padding: 8px; vertical-align: top;">Learning Outcomes</th>
                    <th style="border: 1px solid #e5e7eb; padding: 8px; vertical-align: top;">Instructional Materials</th>
                    <th style="border: 1px solid #e5e7eb; padding: 8px; vertical-align: top;">Method/Learning Styles</th>
                    <th style="border: 1px solid #e5e7eb; padding: 8px; vertical-align: top;">Assessment Criteria</th>
                </tr>
            </thead>
            <tbody>
      `;
      lessonsData.forEach(lesson => {
        // Escape HTML entities to prevent breakage and replace newlines with <br/> for display within cells
        const escapeHtml = (unsafe) => {
            return unsafe
                .replace(/&/g, "&amp;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;")
                .replace(/"/g, "&quot;")
                .replace(/'/g, "&#039;")
                .replace(/\n/g, "<br/>"); // Convert newlines to <br/> for HTML table rendering
        };

        combinedReportLessonTableHtml += `<tr style="border-bottom: 1px solid #e5e7eb;">
            <td style="border: 1px solid #e5e7eb; padding: 8px; vertical-align: top;">${escapeHtml(lesson.lesson || 'N/A')}</td>
            <td style="border: 1px solid #e5e7eb; padding: 8px; vertical-align: top;">${escapeHtml(lesson.topic || 'N/A')}</td>
            <td style="border: 1px solid #e5e7eb; padding: 8px; vertical-align: top;">${escapeHtml(lesson.timeEnvironment || 'N/A')}</td>
            <td style="border: 1px solid #e5e7eb; padding: 8px; vertical-align: top;">${escapeHtml(lesson.contentDescription || 'N/A')}</td>
            <td style="border: 1px solid #e5e7eb; padding: 8px; vertical-align: top;">${escapeHtml(lesson.learningOutcomes || 'N/A')}</td>
            <td style="border: 1px solid #e5e7eb; padding: 8px; vertical-align: top;">${escapeHtml(lesson.instructionalMaterials || 'N/A')}</td>
            <td style="border: 1px solid #e5e7eb; padding: 8px; vertical-align: top;">${escapeHtml(lesson.methodLearningStyles || 'N/A')}</td>
            <td style="border: 1px solid #e5e7eb; padding: 8px; vertical-align: top;">${escapeHtml(lesson.assessmentCriteria || 'N/A')}</td>
        </tr>`;
      });
      combinedReportLessonTableHtml += `
            </tbody>
        </table>
      `;
    }

    // Prompt for the AI to generate the non-table parts of the report (Markdown format)
    const prompt = `As an expert instructional designer, compile a comprehensive instructional design report for the following online, asynchronous, video-recorded course, intended for an LMS environment. Ensure the content format is easy to read and understand by both subject matter experts (like engineers) and other instructional designers. Use clear, concise language, and robust Markdown formatting.

    The report should include the following sections with appropriate Markdown headings and lists for readability:

    # Instructional Design Report: ${courseOutlineData.title || 'Untitled Course'}

    ## 1. Course Overview

    * **Course Title**: ${courseOutlineData.title || 'N/A'}
    * **Target Audience**: ${courseOutlineData.targetAudience || 'N/A'}
    * **Needs Analysis**: ${courseOutlineData.needsAnalysis || 'N/A'}
    * **Course Purpose**: This course aims to provide learners with the necessary knowledge and skills to address the identified needs, delivered asynchronously via video within an LMS.

    ## 2. Overall Learning Objectives

    Upon successful completion of this course, learners will be able to:
    ${courseOutlineData.learningObjectives.map(obj => `- ${obj}`).join('\n') || '- No specific learning objectives provided yet.'}

    ## 3. High-Level Content Outline

    ${courseOutlineData.contentOutline || 'No high-level content outline provided yet.'}

    ## 4. Overall Assessment Strategy

    ${courseOutlineData.assessmentPlan || 'No overall assessment plan provided yet.'}

    ## 5. IACET Considerations & Asynchronous Best Practices

    This section details how the course design aligns with IACET standards and best practices for online, asynchronous, video-recorded learning within an LMS, focusing on engagement, completion, progress tracking, and feedback.
    ${courseOutlineData.iacetConsiderations || 'No specific IACET considerations provided yet.'}
    `; // DO NOT include the table markdown here, it will be added as HTML directly

    try {
      const payload = {
        contents: [{ role: 'user', parts: [{ text: prompt }] }],
      };

      // Changed apiKey to an empty string to allow Canvas to inject it
      const apiKey = "AIzaSyBjZEUkQMyjxXfjJL6GDEIB7B9LKP-b3fA";
      const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (result.candidates && result.candidates.length > 0 &&
          result.candidates[0].content && result.candidates[0].content.parts &&
          result.candidates[0].content.parts.length > 0) {
        const aiReportMarkdown = result.candidates[0].content.parts[0].text;
        // Combine AI-generated markdown with the manually created HTML table
        setCombinedReport(aiReportMarkdown + combinedReportLessonTableHtml);
      } else {
        console.error('Unexpected API response structure for combined report:', result);
        setCombinedReport('Error: Could not generate the combined report. Please try again.');
      }
    } catch (error) {
      console.error('Error generating combined report:', error);
      setCombinedReport('Error: Failed to generate the combined report. Please check your network and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Function to download the full report (MODIFIED TO USE HTML-DOCX-JS)
  const downloadFullReport = async () => {
    if (!combinedReport) {
      setReportCopyMessage('Nothing to download!');
      setTimeout(() => setReportCopyMessage('Download File'), 2000);
      return;
    }

    setReportCopyMessage('Generating DOCX...'); // Update button text during generation

    // Basic Markdown to HTML conversion for the non-table parts
    // This is a simplified conversion. For full Markdown support, a dedicated library (e.g., 'marked') would be needed.
    let htmlContentForDocx = combinedReport
        .replace(/^#\s+(.*)$/gm, '<h1>$1</h1>')   // H1
        .replace(/^##\s+(.*)$/gm, '<h2>$1</h2>')  // H2
        .replace(/^###\s+(.*)$/gm, '<h3>$3</h3>') // H3
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // Bold
        .replace(/\*(.*?)\*/g, '<em>$1</em>')     // Italic
        .replace(/^- (.*$)/gm, '<li>$1</li>');    // Simple list items

    // Simple wrapping of list items in <ul> tags.
    htmlContentForDocx = htmlContentForDocx.replace(/((?:^|\n)(?!<\/?h\d>)(?!<table)[\s\S]*?)((?:^|\n)<li>.*?<\/li>(\s*<li>.*?<\/li>)*)/gm, (match, preContent, listContent) => {
      let processedPreContent = preContent.trim();
      if (processedPreContent && !processedPreContent.match(/<\/?(p|h\d|ul|ol|table)>/)) {
          processedPreContent = `<p>${processedPreContent}</p>`;
      }
      const processedListContent = `<ul>${listContent.replace(/\s*<li>/g, '<li>')}</ul>`;
      return processedPreContent + processedListContent;
    });

    // Simple wrapping of bare text blocks into paragraphs.
    htmlContentForDocx = htmlContentForDocx.split('\n\n').map(block => {
        if (!block.trim().startsWith('<') && block.trim() !== '') {
            return `<p>${block.trim()}</p>`;
        }
        return block;
    }).join('\n');


    // Check if html-docx.js and FileSaver.js are loaded globally
    const docxAsBlob = window.htmlDocx ? window.htmlDocx.asBlob : null;
    const fileSaverAsSaveAs = window.saveAs || null;

    if (!docxAsBlob || !fileSaverAsSaveAs) {
        console.error("html-docx-js or FileSaver.js is not loaded. Please ensure CDN scripts are correctly added to index.html.");
        setReportCopyMessage('Error: Libraries not loaded!');
        setTimeout(() => setReportCopyMessage('Download Report File'), 3000);
        return;
    }


    // Construct the full HTML document string required by html-docx-js
    const fullHtmlDocument = `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <title>${courseOutlineData.title || 'Instructional Design Report'}</title>
    <style>
        body {
            font-family: 'Inter', sans-serif;
            line-height: 1.6;
            color: #374151;
            margin: 2rem;
        }
        h1, h2, h3, h4, h5, h6 {
          color: #1f2937;
          margin-top: 1.5em;
          margin-bottom: 0.5em;
        }
        h1 { font-size: 2.25rem; }
        h2 { font-size: 1.875rem; }
        h3 { font-size: 1.5rem; }
        strong {
          color: #1f2937;
        }
        ul, ol {
          padding-left: 1.5em;
          margin-top: 0.5em;
          margin-bottom: 1em;
        }
        li {
          margin-bottom: 0.5em;
        }
        p {
          margin-bottom: 1em;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 1em;
          margin-bottom: 1em;
          table-layout: auto;
        }
        th, td {
          border: 1px solid #e5e7eb;
          padding: 8px;
          text-align: left;
          vertical-align: top;
          word-wrap: break-word;
          overflow-wrap: break-word;
        }
        th {
          background-color: #f9fafb;
          font-weight: 600;
          color: #4b5563;
        }
    </style>
</head>
<body>
    <div class="document-content">
        ${htmlContentForDocx}
    </div>
</body>
</html>`;

    try {
        const blob = await docxAsBlob(fullHtmlDocument);
        // Create a safe filename from the course title
        const filename = `full_instructional_design_report_${courseOutlineData.title.replace(/[^a-z0-9]/gi, '_').toLowerCase() || 'untitled'}.docx`;
        fileSaverAsSaveAs(blob, filename); // Trigger the download
        setReportCopyMessage('Downloaded DOCX!');
        setTimeout(() => setReportCopyMessage('Download Report File'), 3000);
    } catch (error) {
        console.error('Error generating DOCX:', error);
        setReportCopyMessage('Error generating DOCX!');
        setTimeout(() => setReportCopyMessage('Download Report File'), 3000);
    }
  };

  // Function to email the detailed lesson plan
  const emailDetailedLessonPlan = () => {
    const csvContent = generateLessonsCsv(); // Get the latest CSV content
    if (!csvContent.trim()) { // Check if the generated CSV content is empty or just whitespace
      setEmailLessonPlanMessage('No data to email!');
      setTimeout(() => setEmailLessonPlanMessage('Email Lesson Plan'), 3000);
      console.warn('Attempted to email empty detailed lesson plan CSV.');
      return;
    }
    const subject = `Detailed Lesson Plan for: ${courseOutlineData.title || 'Untitled Course'}`;
    const body = `Hello Cheryl,\n\nPlease find the detailed lesson plan for the course "${courseOutlineData.title || 'Untitled Course'}" below.\n\n${csvContent}\n\nBest regards,\n[Your Name]`;
    emailContent('cheryl@myplctraining.com', subject, body, setEmailLessonPlanMessage);
  };

  // --- Render Logic ---
  const renderContent = () => {
    switch (currentView) {
      case 'outline':
        return (
          <div className="flex flex-col h-full bg-gray-50 p-4 rounded-lg shadow-inner overflow-hidden">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 border-b pb-2">Course Outline Assistant</h2>
            <p className="text-gray-600 mb-4">
              Begin your course design by interacting with our AI assistant. Answer the prompts to define your course's core elements, including its title, target audience, learning objectives, and overall content structure. The AI will guide you through each step.
            </p>
            <div className="flex-1 overflow-y-auto pr-4 custom-scrollbar">
              {chatHistory.map((message, index) => (
                <div
                  key={index}
                  className={`mb-4 p-3 rounded-lg max-w-[80%] ${
                    message.role === 'user'
                      ? 'bg-purple-100 self-end ml-auto text-gray-800'
                      : 'bg-white self-start mr-auto text-gray-700 shadow-sm'
                  }`}
                  dangerouslySetInnerHTML={{ __html: message.parts[0].text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }}
                />
              ))}
              {isLoading && (
                <div className="self-start mr-auto mb-4 p-3 rounded-lg bg-white shadow-sm flex items-center">
                  <div className="dot-pulse"></div>
                  <span className="ml-3 text-gray-500">AI is thinking...</span>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
            <div className="mt-4 flex items-center">
              {currentOutlineStep < outlineSteps.length ? (
                <>
                  <input
                    type="text"
                    className="flex-1 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    value={userInput}
                    onChange={(e) => setUserInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && !isLoading && sendOutlineMessage()}
                    placeholder={outlineSteps[currentOutlineStep]?.placeholder || "Type your response..."}
                    disabled={isLoading}
                  />
                  <button
                    className="ml-3 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
                    onClick={sendOutlineMessage}
                    disabled={isLoading}
                  >
                    Send
                  </button>
                </>
              ) : (
                <button
                  className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleProceedToLessons}
                  disabled={isLoading}
                >
                  Proceed to Lesson Details
                </button>
              )}
            </div>
          </div>
        );
      case 'lessons':
        return (
          <div className="flex flex-col h-full bg-gray-50 p-4 rounded-lg shadow-inner overflow-hidden">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 border-b pb-2">Detailed Lesson Plan</h2>
            <p className="text-gray-600 mb-4">
              Here, you can create and refine the individual lessons of your course.
              Use "Auto-Generate Lessons" to get a head start based on your course outline, or "Add Lesson Row" to add entries manually.
              Edit any field directly in the table. You can copy the table data as CSV for use in spreadsheet software.
            </p>
            <div className="flex justify-start items-center mb-4 flex-wrap gap-2">
              <button
                className="px-5 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition duration-200 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={autoGenerateLessons}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="dot-pulse small-dot-pulse mr-2"></div>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V8z" clipRule="evenodd" />
                  </svg>
                )}
                {isLoading ? 'Generating...' : 'Auto-Generate Lessons'}
              </button>
              <button
                className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition duration-200 flex items-center"
                onClick={addLesson}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
                Add Lesson Row
              </button>
              <button
                className="px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition duration-200 flex items-center"
                onClick={copyLessonsCsvToClipboard}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M9 2a2 2 0 00-2 2v8a2 2 0 002 2h2a2 2 0 002-2V4a2 2 0 00-2-2H9z" />
                  <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
                {csvActionMessage}
              </button>
              <button
                className="px-5 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition duration-200 flex items-center"
                onClick={downloadLessonsCsv}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
                Download CSV File
              </button>
              {/* Email Lesson Plan button is intentionally removed */}
            </div>
            <div className="flex-1 overflow-x-auto overflow-y-auto custom-scrollbar">
              <table className="min-w-full bg-white border border-gray-200">
                <thead>
                  <tr className="bg-gray-100 text-gray-600 uppercase text-sm leading-normal">
                    <th className="py-3 px-6 text-left">Lesson</th>
                    <th className="py-3 px-6 text-left">Topic</th>
                    <th className="py-3 px-6 text-left">Time/Environment</th>
                    <th className="py-3 px-6 text-left">Content Description/Purpose</th>
                    <th className="py-3 px-6 text-left">Learning Outcomes</th>
                    <th className="py-3 px-6 text-left">Instructional Materials</th>
                    <th className="py-3 px-6 text-left">Method/Learning Styles</th>
                    <th className="py-3 px-6 text-left">Assessment Criteria</th>
                  </tr>
                </thead>
                <tbody className="text-gray-600 text-sm font-light">
                  {lessonsData.map((lesson, index) => (
                    <tr key={index} className="border-b border-gray-200 hover:bg-gray-100">
                      <td className="py-3 px-6 text-left whitespace-nowrap">
                        <input
                          type="text"
                          value={lesson.lesson}
                          onChange={(e) => handleLessonChange(index, 'lesson', e.target.value)}
                          className="w-full bg-transparent border-none focus:ring-0"
                        />
                      </td>
                      <td className="py-3 px-6 text-left">
                        <textarea
                          value={lesson.topic}
                          onChange={(e) => handleLessonChange(index, 'topic', e.target.value)}
                          className="w-full bg-transparent border-none focus:ring-0 resize-y min-h-[40px]"
                        />
                      </td>
                      <td className="py-3 px-6 text-left">
                        <textarea
                          value={lesson.timeEnvironment}
                          onChange={(e) => handleLessonChange(index, 'timeEnvironment', e.target.value)}
                          className="w-full bg-transparent border-none focus:ring-0 resize-y min-h-[40px]"
                        />
                      </td>
                      <td className="py-3 px-6 text-left">
                        <textarea
                          value={lesson.contentDescription}
                          onChange={(e) => handleLessonChange(index, 'contentDescription', e.target.value)}
                          className="w-full bg-transparent border-none focus:ring-0 resize-y min-h-[40px]"
                        />
                      </td>
                      <td className="py-3 px-6 text-left">
                        <textarea
                          value={lesson.learningOutcomes}
                          onChange={(e) => handleLessonChange(index, 'learningOutcomes', e.target.value)}
                          className="w-full bg-transparent border-none focus:ring-0 resize-y min-h-[40px]"
                        />
                      </td>
                      <td className="py-3 px-6 text-left">
                        <textarea
                          value={lesson.instructionalMaterials}
                          onChange={(e) => handleLessonChange(index, 'instructionalMaterials', e.target.value)}
                          className="w-full bg-transparent border-none focus:ring-0 resize-y min-h-[40px]"
                        />
                      </td>
                      <td className="py-3 px-6 text-left">
                        <textarea
                          value={lesson.methodLearningStyles}
                          onChange={(e) => handleLessonChange(index, 'methodLearningStyles', e.target.value)}
                          className="w-full bg-transparent border-none focus:ring-0 resize-y min-h-[40px]"
                        />
                      </td>
                      <td className="py-3 px-6 text-left">
                        <textarea
                          value={lesson.assessmentCriteria}
                          onChange={(e) => handleLessonChange(index, 'assessmentCriteria', e.target.value)}
                          className="w-full bg-transparent border-none focus:ring-0 resize-y min-h-[40px]"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      case 'validation':
        return (
          <div className="flex flex-col h-full bg-gray-50 p-4 rounded-lg shadow-inner overflow-hidden">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 border-b pb-2">Instructional Design Validation Report</h2>
            <p className="text-gray-600 mb-4">
              Our AI acts as a quality assurance specialist, reviewing your course outline and detailed lesson plan.
              It identifies strengths, areas for improvement, and provides actionable recommendations.
              Click "Apply Recommendations to Lesson Plan" to have the AI automatically update your lesson details based on its suggestions.
            </p>
            <div className="flex justify-start items-center mb-4 flex-wrap gap-2"> {/* New flex container for buttons */}
              <button
                className="px-5 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
                onClick={applyValidationRecommendations}
                disabled={isLoading || !validationReport}
              >
                {isLoading && currentView === 'validation' ? (
                  <div className="dot-pulse small-dot-pulse mr-2"></div>
                ) : applyRecsMessage}
              </button>
              <button
                className="px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition duration-200 flex items-center"
                onClick={downloadValidationReport}
                disabled={!validationReport || isLoading}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
                {validationReportDownloadMessage}
              </button>
            </div>
            {isLoading ? (
              <div className="flex justify-center items-center h-full">
                <div className="dot-pulse"></div>
                <span className="ml-3 text-gray-500">Generating validation report...</span>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto pr-4 custom-scrollbar">
                {validationReport ? (
                  <div
                    className="prose lg:prose-lg max-w-none"
                    dangerouslySetInnerHTML={{ __html: validationReport.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\n/g, '<br/>') }}
                  />
                ) : (
                  <p className="text-gray-500">Click "Generate Validation Report" to get started.</p>
                )}
              </div>
            )}
          </div>
        );
      case 'suggestions':
        return (
          <div className="flex flex-col h-full bg-gray-50 p-4 rounded-lg shadow-inner overflow-hidden">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 border-b pb-2">AI Suggestions for Lessons & Storyline</h2>
            <p className="text-gray-600 mb-4">
              Get creative insights and ideas from our AI to enhance your course.
              This section offers suggestions to improve lesson content, refine the overall course storyline and flow,
              and explore innovative instructional methods suitable for asynchronous learning.
              Click "Copy to Clipboard" to easily transfer the suggestions.
            </p>
            <button
                className="mt-4 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200 w-full mb-4"
                onClick={() => copyToClipboard(aiSuggestionsReport, setAiSuggestionsCopyMessage)}
                disabled={!aiSuggestionsReport || isLoading}
            >
              {aiSuggestionsCopyMessage}
            </button>
            {isLoading ? (
              <div className="flex justify-center items-center h-full">
                <div className="dot-pulse"></div>
                <span className="ml-3 text-gray-500">Generating AI suggestions...</span>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto pr-4 custom-scrollbar">
                {aiSuggestionsReport ? (
                  <div
                    className="prose lg:prose-lg max-w-none"
                    dangerouslySetInnerHTML={{ __html: aiSuggestionsReport.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\n/g, '<br/>') }}
                  />
                ) : (
                  <p className="text-gray-500">Click "Get AI Suggestions" to generate ideas.</p>
                )}
              </div>
            )}
          </div>
        );
      case 'scriptAssistant':
        return (
          <div className="flex flex-col h-full bg-gray-50 p-4 rounded-lg shadow-inner overflow-hidden">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 border-b pb-2">Video Script Assistant</h2>
            <p className="text-gray-600 mb-4">
              Generate a concise video script for any of your detailed lessons.
              Select a lesson from the dropdown, then click "Draft Video Script."
              The AI will provide a script optimized for clarity, engagement, and video format, including suggestions for visuals.
              Copy the generated script to your clipboard for easy use.
            </p>
            <div className="mb-4">
              <label htmlFor="script-lesson-select" className="block text-gray-700 text-sm font-bold mb-2">
                Select Lesson for Script:
              </label>
              <select
                id="script-lesson-select"
                className="shadow border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                value={scriptLessonIndex}
                onChange={(e) => setScriptLessonIndex(Number(e.target.value))}
                disabled={lessonsData.length === 0 || isLoading}
              >
                {lessonsData.length === 0 || (lessonsData.length === 1 && lessonsData[0].lesson === '') ? (
                  <option value="-1" disabled>No lessons defined yet. Please auto-generate or add lessons.</option>
                ) : (
                  lessonsData.map((lesson, index) => (
                    <option key={index} value={index}>
                      {lesson.lesson || `Lesson ${index + 1}`} - {lesson.topic || 'No Topic'}
                    </option>
                  ))
                )}
              </select>
              <button
                className="mt-4 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200 w-full"
                onClick={generateVideoScript}
                disabled={isLoading || lessonsData.length === 0 || (lessonsData.length === 1 && lessonsData[0].lesson === '')}
              >
                {isLoading ? (
                  <div className="dot-pulse small-dot-pulse mx-auto"></div>
                ) : 'Draft Video Script'}
              </button>
              <button
                className="mt-4 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200 w-full"
                onClick={() => copyToClipboard(generatedVideoScript, setScriptCopyMessage)}
                disabled={!generatedVideoScript || isLoading}
              >
                {scriptCopyMessage}
              </button>
            </div>
            {isLoading ? null : (
              <div className="flex-1 overflow-y-auto pr-4 custom-scrollbar">
                {generatedVideoScript ? (
                  <div
                    className="prose lg:prose-lg max-w-none"
                    dangerouslySetInnerHTML={{ __html: generatedVideoScript.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\n/g, '<br/>') }}
                  />
                ) : (
                  <p className="text-gray-500">Generated video script will appear here.</p>
                )}
              </div>
            )}
          </div>
        );
      case 'quizGenerator':
        return (
          <div className="flex flex-col h-full bg-gray-50 p-4 rounded-lg shadow-inner overflow-hidden">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 border-b pb-2">Quiz Question Generator</h2>
            <p className="text-gray-600 mb-4">
              Create assessment questions for your lessons.
              Select a lesson from the dropdown, then click "Generate Quiz Questions."
              The AI will provide multiple-choice questions designed to assess the learning outcomes of that specific lesson.
            </p>
            <div className="mb-4">
              <label htmlFor="quiz-lesson-select" className="block text-gray-700 text-sm font-bold mb-2">
                Select Lesson for Quiz:
              </label>
              <select
                id="quiz-lesson-select"
                className="shadow border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                value={quizLessonIndex}
                onChange={(e) => setQuizLessonIndex(Number(e.target.value))}
                disabled={lessonsData.length === 0 || isLoading}
              >
                {lessonsData.length === 0 || (lessonsData.length === 1 && lessonsData[0].lesson === '') ? (
                  <option value="-1" disabled>No lessons defined yet. Please auto-generate or add lessons.</option>
                ) : (
                  lessonsData.map((lesson, index) => (
                    <option key={index} value={index}>
                      {lesson.lesson || `Lesson ${index + 1}`} - {lesson.topic || 'No Topic'}
                    </option>
                  ))
                )}
              </select>
              <button
                className="mt-4 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200 w-full"
                onClick={generateQuizQuestions}
                disabled={isLoading || lessonsData.length === 0 || (lessonsData.length === 1 && lessonsData[0].lesson === '')}
              >
                {isLoading ? (
                  <div className="dot-pulse small-dot-pulse mx-auto"></div>
                ) : 'Generate Quiz Questions'}
              </button>
            </div>
            {isLoading ? (
              <div className="flex justify-center items-center h-full">
                <div className="dot-pulse"></div>
                <span className="ml-3 text-gray-500">Generating quiz questions...</span>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto pr-4 custom-scrollbar">
                {generatedQuizQuestions ? (
                  <div
                    className="prose lg:prose-lg max-w-none"
                    dangerouslySetInnerHTML={{ __html: generatedQuizQuestions.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\n/g, '<br/>') }}
                  />
                ) : (
                  <p className="text-gray-500">Generated quiz questions will appear here.</p>
                )}
              </div>
            )}
          </div>
        );
      case 'report':
        // For the report view, we'll display the combined report after a basic Markdown-to-HTML conversion.
        // This won't be perfect, but it will reflect what html-docx-js is trying to process.
        const displayReportContent = combinedReport
          .replace(/^#\s+(.*)$/gm, '<h1>$1</h1>')   // H1
          .replace(/^##\s+(.*)$/gm, '<h2>$1</h2>')  // H2
          .replace(/^###\s+(.*)$/gm, '<h3>$1</h3>') // H3
          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // Bold
          .replace(/\*(.*?)\*/g, '<em>$1</em>');     // Italic

        return (
          <div className="flex flex-col h-full bg-gray-50 p-4 rounded-lg shadow-inner overflow-hidden">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 border-b pb-2">Full Instructional Design Report</h2>
            <p className="text-gray-600 mb-4">
              This comprehensive report compiles all the information from your course outline and detailed lesson plan into a single, professional document.
              You can easily copy the report to your clipboard or download it as a Word-compatible file.
            </p>
            <div className="flex justify-start items-center mb-4 flex-wrap gap-2"> {/* Changed justify-between to justify-start */}
              <button
                className="px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition duration-200 flex items-center"
                onClick={() => copyToClipboard(combinedReport, setReportCopyMessage)} // Copy raw combined string
                disabled={!combinedReport || isLoading}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M9 2a2 2 0 00-2 2v8a2 2 0 002 2h2a2 2 0 002-2V4a2 2 0 00-2-2H9z" />
                  <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
                {reportCopyMessage}
              </button>
              <button
                className="px-5 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition duration-200 flex items-center"
                onClick={downloadFullReport} // This will now trigger DOCX generation
                disabled={!combinedReport || isLoading}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
                Download Report File
              </button>
              {/* Removed Email Full Report button */}
            </div>
            {isLoading ? (
              <div className="flex justify-center items-center h-full">
                <div className="dot-pulse"></div>
                <span className="ml-3 text-gray-500">Generating full report...</span>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto pr-4 custom-scrollbar">
                {combinedReport ? (
                  // Display the HTML-converted content here, which should reflect the DOCX structure
                  <div
                    className="prose lg:prose-lg max-w-none"
                    dangerouslySetInnerHTML={{ __html: displayReportContent }}
                  />
                ) : (
                  <p className="text-gray-500">Click "Generate Full Report" to compile all design elements.</p>
                )}
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-8 flex font-sans"> {/* Added font-sans for Inter */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
        body {
          font-family: 'Inter', sans-serif;
        }
        /* Custom Scrollbar */
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #888;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #555;
        }

        /* Tailwind Prose adjustments for better markdown rendering */
        .prose h1, .prose h2, .prose h3, .prose h4, .prose h5, .prose h6 {
          color: #333;
          margin-top: 1.5em;
          margin-bottom: 0.5em;
        }
        .prose strong {
          color: #333;
        }
        .prose ul, .prose ol {
          padding-left: 1.5em;
        }
        .prose li {
          margin-bottom: 0.5em;
        }
        .prose p {
          margin-bottom: 1em;
        }
        .prose table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 1em;
          margin-bottom: 1em;
        }
        .prose th, .prose td {
          border: 1px solid #ddd;
          padding: 8px;
          text-align: left;
          vertical-align: top; /* Ensure content aligns to top in table cells */
        }
        .prose th {
          background-color: #f2f2f2;
          font-weight: bold;
        }

        /* Dot Pulse Animation */
        .dot-pulse {
          position: relative;
          width: 10px;
          height: 10px;
          border-radius: 5px;
          background-color: #a78bfa;
          color: #a78bfa;
          box-shadow: 0 0 0 0 rgba(167, 139, 250, 0.7);
          animation: dot-pulse-shadow 1.5s infinite linear;
        }

        .dot-pulse::before, .dot-pulse::after {
          content: '';
          position: absolute;
          width: 10px;
          height: 10px;
          border-radius: 5px;
          background-color: #a78bfa;
          color: #a78bfa;
        }

        .dot-pulse::before {
          left: -15px;
          box-shadow: 0 0 0 0 rgba(167, 139, 250, 0.7);
          animation: dot-pulse-shadow 1.5s infinite linear;
          animation-delay: 0.25s;
        }

        .dot-pulse::after {
          right: -15px;
          box-shadow: 0 0 0 0 rgba(167, 139, 250, 0.7);
          animation: dot-pulse-shadow 1.5s infinite linear;
          animation-delay: 0.5s;
        }

        @keyframes dot-pulse-shadow {
          0% {
            box-shadow: 0 0 0 0 rgba(167, 139, 250, 0.7);
          }
          25% {
            box-shadow: 0 0 0 5px rgba(167, 139, 250, 0.7);
          }
          50% {
            box-shadow: 0 0 0 0 rgba(167, 139, 250, 0.7);
          }
          75% {
            box-shadow: 0 0 0 5px rgba(167, 139, 250, 0.7);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(167, 139, 250, 0.7);
          }
        }

        .small-dot-pulse {
          transform: scale(0.7);
          margin: 0 5px;
        }
        .small-dot-pulse::before {
          left: -10px;
        }
        .small-dot-pulse::after {
          right: -10px;
        }
      `}</style>
      <nav className="w-64 bg-white p-6 shadow-md rounded-lg flex flex-col justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-purple-700 mb-6 text-center leading-tight">myPLCtraining.com Course Creation assistant</h1>
          <ul className="space-y-3">
            <li>
              <button
                className={`w-full text-left px-4 py-2 rounded-md transition-colors duration-200 ${
                  currentView === 'outline'
                    ? 'bg-purple-100 text-purple-800 font-semibold'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
                onClick={() => setCurrentView('outline')}
              >
                1. Course Outline (Chat)
              </button>
            </li>
            <li>
              <button
                className={`w-full text-left px-4 py-2 rounded-md transition-colors duration-200 ${
                  currentView === 'lessons'
                    ? 'bg-purple-100 text-purple-800 font-semibold'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
                onClick={() => setCurrentView('lessons')}
              >
                2. Detailed Lesson Plan
              </button>
            </li>
            <li>
              <button
                className={`w-full text-left px-4 py-2 rounded-md transition-colors duration-200 ${
                  currentView === 'validation'
                    ? 'bg-purple-100 text-purple-800 font-semibold'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
                onClick={generateValidationReport}
                disabled={isLoading}
              >
                {isLoading && currentView === 'validation' ? 'Generating...' : '3. Validation Report'}
              </button>
            </li>
            <li>
              <button
                className={`w-full text-left px-4 py-2 rounded-md transition-colors duration-200 ${
                  currentView === 'suggestions'
                    ? 'bg-purple-100 text-purple-800 font-semibold'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
                onClick={getAiSuggestions}
                disabled={isLoading}
              >
                {isLoading && currentView === 'suggestions' ? 'Generating...' : '4. AI Suggestions (Optional)'}
              </button>
            </li>
            <li>
              <button
                className={`w-full text-left px-4 py-2 rounded-md transition-colors duration-200 ${
                  currentView === 'scriptAssistant'
                    ? 'bg-purple-100 text-purple-800 font-semibold'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
                onClick={() => setCurrentView('scriptAssistant')}
              >
                5. Video Script Assistant (Optional)
              </button>
            </li>
            <li>
              <button
                className={`w-full text-left px-4 py-2 rounded-md transition-colors duration-200 ${
                  currentView === 'quizGenerator'
                    ? 'bg-purple-100 text-purple-800 font-semibold'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
                onClick={() => setCurrentView('quizGenerator')}
              >
                6. Quiz Generator (Optional)
              </button>
            </li>
          </ul>
        </div>
        <div className="mt-8">
          <button
            className={`w-full text-left px-4 py-3 rounded-md transition-colors duration-200 text-white font-bold flex items-center justify-center ${
              currentView === 'report'
                ? 'bg-indigo-600'
                : 'bg-indigo-500 hover:bg-indigo-600'
            }`}
            onClick={generateCombinedReport}
            disabled={isLoading}
          >
            {isLoading && currentView === 'report' ? (
              <div className="dot-pulse small-dot-pulse mr-2"></div>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0113 2.586L16.414 6A2 2 0 0117 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 2.5V4h3.586L14 7.414V16H6V6.5z" clipRule="evenodd" />
              </svg>
            )}
            {isLoading && currentView === 'report' ? 'Compiling Report...' : 'Generate Full Report'}
          </button>
        </div>
      </nav>

      <main className="flex-1 ml-8 h-[90vh]">
        {renderContent()}
      </main>
    </div>
  );
};

export default App;
